import { useTranslation } from 'react-i18next';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

export default function About() {
  const { t } = useTranslation();

  const storyAnim = useScrollAnimation();
  const missionAnim = useScrollAnimation();
  const valuesAnim = useScrollAnimation();
  const statsAnim = useScrollAnimation();

  const values = [
    { key: 'integrity', icon: 'ri-shield-check-line' },
    { key: 'excellence', icon: 'ri-medal-line' },
    { key: 'innovation', icon: 'ri-lightbulb-line' },
    { key: 'commitment', icon: 'ri-hand-heart-line' },
  ];

  const stats = [
    { key: 'years', value: '8+', label: t('about.stats.years') },
    { key: 'services', value: '11', label: t('about.stats.services') },
    { key: 'clients', value: '8+', label: t('about.stats.clients') },
    { key: 'support', value: '24/7', label: t('about.stats.support') },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative h-[360px] md:h-[480px] flex items-center justify-center bg-primary-950">
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20geometric%20engineering%20structures%20with%20steel%20blue%20and%20dark%20charcoal%20color%20palette%2C%20subtle%20copper%20highlights%2C%20modern%20minimalist%20architectural%20composition%20with%20clean%20lines%2C%20professional%20technical%20aesthetic%20inspired%20by%20African%20diversity&width=1600&height=900&seq=about-hero-deiaspart&orientation=landscape"
            alt="About DEIASPART"
            className="w-full h-full object-cover opacity-30"
          />
        </div>
        <div className="relative z-10 text-center px-4">
          <h1 className="font-heading font-bold text-3xl md:text-5xl lg:text-6xl text-background-50 mb-3">
            {t('about.hero.title')}
          </h1>
          <p className="text-background-300 text-base md:text-lg max-w-xl mx-auto">
            {t('about.hero.subtitle')}
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="py-16 md:py-24 bg-background-50">
        <div className="w-full px-4 md:px-6 lg:px-10">
          <div
            ref={storyAnim.ref}
            className={`max-w-3xl mx-auto text-center transition-all duration-700 ${
              storyAnim.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <span className="text-accent-500 font-medium text-sm tracking-wider uppercase mb-3 block">
              DEIASPART
            </span>
            <h2 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-foreground-950 mb-8">
              {t('about.story.title')}
            </h2>
            <p className="text-foreground-700 leading-relaxed mb-5 text-base md:text-lg">
              {t('about.story.text1')}
            </p>
            <p className="text-foreground-700 leading-relaxed text-base md:text-lg">
              {t('about.story.text2')}
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 md:py-24 bg-background-100">
        <div className="w-full px-4 md:px-6 lg:px-10">
          <div
            ref={missionAnim.ref}
            className={`grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 max-w-5xl mx-auto transition-all duration-700 ${
              missionAnim.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <div className="bg-background-50 rounded-2xl p-6 md:p-10 border border-background-200/70">
              <div className="w-12 h-12 rounded-lg bg-primary-50 flex items-center justify-center mb-5">
                <i className="ri-focus-3-line text-primary-500 text-xl w-6 h-6 flex items-center justify-center"></i>
              </div>
              <h3 className="font-heading font-semibold text-xl md:text-2xl text-foreground-950 mb-4">
                {t('about.mission.title')}
              </h3>
              <p className="text-foreground-600 leading-relaxed text-sm md:text-base">
                {t('about.mission.text')}
              </p>
            </div>
            <div className="bg-background-50 rounded-2xl p-6 md:p-10 border border-background-200/70">
              <div className="w-12 h-12 rounded-lg bg-accent-50 flex items-center justify-center mb-5">
                <i className="ri-eye-line text-accent-500 text-xl w-6 h-6 flex items-center justify-center"></i>
              </div>
              <h3 className="font-heading font-semibold text-xl md:text-2xl text-foreground-950 mb-4">
                {t('about.vision.title')}
              </h3>
              <p className="text-foreground-600 leading-relaxed text-sm md:text-base">
                {t('about.vision.text')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 md:py-24 bg-background-50">
        <div className="w-full px-4 md:px-6 lg:px-10">
          <div
            ref={valuesAnim.ref}
            className={`text-center mb-12 md:mb-16 transition-all duration-700 ${
              valuesAnim.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <h2 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-foreground-950 mb-4">
              {t('about.values.title')}
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6 max-w-5xl mx-auto">
            {values.map((value, i) => (
              <div
                key={value.key}
                className={`bg-background-100 rounded-xl p-6 border border-background-200/70 text-center transition-all duration-700 hover:-translate-y-1 ${
                  valuesAnim.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="w-12 h-12 rounded-lg bg-primary-50 flex items-center justify-center mx-auto mb-4">
                  <i className={`${value.icon} text-primary-500 text-xl w-6 h-6 flex items-center justify-center`}></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-950 mb-2 text-base">
                  {t(`about.values.${value.key}`)}
                </h3>
                <p className="text-foreground-600 text-sm leading-relaxed">
                  {t(`about.values.${value.key}Desc`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 md:py-24 bg-primary-950">
        <div className="w-full px-4 md:px-6 lg:px-10">
          <div
            ref={statsAnim.ref}
            className={`grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 max-w-4xl mx-auto transition-all duration-700 ${
              statsAnim.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            {stats.map((stat, i) => (
              <div
                key={stat.key}
                className="text-center"
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <p className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-accent-400 mb-2">
                  {stat.value}
                </p>
                <p className="text-background-300 text-sm md:text-base">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}